// Types for the Immigration Status Helper

export interface StatusCategory {
  id: string;
  name: string;
  description: string;
  riskLevel: 'lawful' | 'protected' | 'at_risk' | 'unknown';
  requiresAttorneyConsult: boolean;
  nextSteps: string;
}

export interface Question {
  id: string;
  orderIndex: number;
  questionText: string;
  questionTextEs?: string;
  helpText?: string;
  questionType: 'single_choice' | 'multiple_choice' | 'yes_no' | 'date' | 'number';
  isRequired: boolean;
  parentQuestionId?: string;
  showIfAnswer?: string;
}

export interface AnswerOption {
  id: string;
  questionId: string;
  optionText: string;
  optionTextEs?: string;
  orderIndex: number;
  leadsToQuestionId?: string;
  contributesToStatus?: string;
  weight: number;
}

export interface LegalOrganization {
  id: string;
  name: string;
  description?: string;
  websiteUrl?: string;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  serviceArea: string[];
  languages: string[];
  specializations: string[];
  isFree: boolean;
  isVerified: boolean;
}

export interface EducationContent {
  id: string;
  title: string;
  titleEs?: string;
  slug: string;
  content: string;
  contentEs?: string;
  category?: string;
  relatedStatusId?: string;
  readingTimeMinutes?: number;
  isPublished: boolean;
}

export interface QuestionnaireAnswer {
  questionId: string;
  answerId: string;
  value?: string | number | Date;
}

export interface QuestionnaireState {
  currentQuestionId: string | null;
  answers: QuestionnaireAnswer[];
  history: string[];
  isComplete: boolean;
  startedAt: Date;
  completedAt?: Date;
}

export interface StatusResult {
  primaryStatus: StatusCategory;
  confidence: 'high' | 'medium' | 'low';
  contributingFactors: string[];
  recommendedActions: string[];
  matchedOrganizations: LegalOrganization[];
}

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface QuestionsResponse {
  questions: Question[];
  options: Record<string, AnswerOption[]>;
}

export interface OrganizationsSearchParams {
  state?: string;
  specialization?: string;
  language?: string;
  freeOnly?: boolean;
}

// Constants
export const RISK_LEVELS = {
  lawful: { label: 'Lawful Status', color: '#22c55e' },
  protected: { label: 'Protected Status', color: '#eab308' },
  at_risk: { label: 'At Risk', color: '#f97316' },
  unknown: { label: 'Status Unclear', color: '#6b7280' },
} as const;

export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
] as const;

export const SPECIALIZATIONS = [
  'DACA',
  'TPS',
  'Asylum',
  'Family Petitions',
  'Naturalization',
  'Deportation Defense',
  'VAWA',
  'U-Visa',
  'Work Permits',
] as const;

export const LANGUAGES = [
  'English',
  'Spanish',
  'Chinese',
  'Vietnamese',
  'Korean',
  'Tagalog',
  'Arabic',
  'French',
  'Haitian Creole',
  'Portuguese',
] as const;
